import classNames from 'classnames';

import DropdownBuilder from 'components/DropdownBuilder';
import React, { useCallback, useMemo } from 'react';
import styles from './SimpleSelect.module.scss';

interface Option<V> {
  value: V;
  label: string;
  icon?: React.ReactNode;
}
interface ProcessedOption<V> {
  value: V | '__placeholder';
  label: string;
  createTrigger?: boolean;
  createValue?: string;
  isSelectable?: boolean;
  icon?: React.ReactNode;
}

interface Props<V> {
  value: V | null;
  options: Array<Option<V>>;
  onChange: (value: V) => unknown;
  onCreate?: (title: string) => unknown;
  placeholder: string; // defaultProp'd,
  onCreatePlaceholder: string; // defaultProp'd,
  disabled?: boolean;
  className?: string;
  hideFromFullstory?: boolean;
  'data-testid'?: string;
}

export default function SimpleSelect<V>(props: Props<V>) {
  const {
    value,
    options,
    disabled,
    onChange,
    onCreate,
    placeholder,
    onCreatePlaceholder,
    className,
    hideFromFullstory,
  } = props;
  const allowCreate = onCreate != null;

  const renderOption = useCallback(
    <V,>(option: ProcessedOption<V>) => {
      if (option.createTrigger) {
        return (
          <div className={styles.rowWithIcon}>
            <div className={styles.optionIcon}>
              <div className={styles.icon}>+</div>
            </div>
            <div className={styles.label} data-testid="simpleselect_option_add_new">
              {option.label}
            </div>
          </div>
        );
      }

      return (
        <div className={classNames(styles.rowWithIcon, { 'fs-mask': hideFromFullstory })}>
          <div className={styles.label} data-testid="simpleselect_option">
            {option.icon && option.icon}
            {option.icon ? ' ' + option.label : option.label}
          </div>
        </div>
      );
    },
    [hideFromFullstory]
  );

  const handleChange = useCallback(
    (newValue: V | '__placeholder' | null, item: ProcessedOption<V> | null) => {
      if (item != null && onCreate != null && item.createTrigger && item.createValue != null) {
        onCreate(item.createValue);
      } else if (item != null && item.value !== '__placeholder') {
        onChange(item.value);
      }
    },
    [onChange, onCreate]
  );
  const postProcessOptions = useCallback(
    (
      filteredRowNodes: Array<any>,
      filterString: string,
      pushOption: (processedOption: ProcessedOption<V>) => void
    ) => {
      if (allowCreate) {
        if (filterString.length > 0) {
          // All filtered out
          pushOption({
            value: '__placeholder',
            label: `Add ${filterString}`,
            createTrigger: true,
            createValue: filterString,
          });
        } else {
          pushOption({
            value: '__placeholder',
            label: onCreatePlaceholder,
            createTrigger: true,
            isSelectable: false,
          });
        }
      }
    },
    [allowCreate, onCreatePlaceholder]
  );

  // Appease flow
  const cast: Array<ProcessedOption<V>> = useMemo(
    () =>
      options.map(
        (option: Option<V>): ProcessedOption<V> => ({
          ...option,
        })
      ),
    [options]
  );

  return (
    <DropdownBuilder<V | '__placeholder', ProcessedOption<V>>
      value={value}
      options={cast}
      onChange={handleChange}
      disabled={disabled}
      renderOption={renderOption}
      postProcessOptions={postProcessOptions}
      placeholder={placeholder}
    >
      {context => (
        <div
          style={{ position: 'relative' }}
          className={className}
          data-testid={props['data-testid'] ?? 'simpleselect'}
        >
          {context.renderInput({
            className: classNames(styles.input, { 'fs-mask': hideFromFullstory }),
            'data-testid': 'simpleselect_input',
          })}
          {context.isOpen && context.renderListPopover()}
        </div>
      )}
    </DropdownBuilder>
  );
}
SimpleSelect.defaultProps = {
  placeholder: 'Select...',
  onCreatePlaceholder: 'Create new by typing...',
};
